/**
 * VÉRAULT Travels — Itinerary Engine (v3)
 * SINGLE SOURCE OF TRUTH for advisor planning + client presentation.
 * 
 * Modes: Overview | Plan Day | Client Preview
 * Day Structure: Morning / Afternoon / Evening blocks
 * Each block: meal_slot + primary_activity + secondary_activity + notes
 * Visibility: draft | suggested | approved | hidden | scheduled_reveal
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sun, Sunset, Moon, Plus, Check, X, ChevronLeft, ChevronRight,
  Eye, EyeOff, Lock, Unlock, Sparkles, Edit2, Trash2, Clock,
  MapPin, Calendar, AlertTriangle, Image, Loader2, Save,
  Coffee, Wine, Utensils, ChevronDown, ChevronUp, Plane,
  ExternalLink, Phone, DollarSign, Search, RefreshCw,
  LayoutGrid, CalendarDays, Monitor
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================
// CONFIGURATION
// ============================================================

const BLOCKS = {
  morning: { label: 'Morning', icon: Sun, accent: '#C5A059', bg: 'bg-[#C5A059]/5', border: 'border-[#C5A059]/20', text: 'text-[#C5A059]' },
  afternoon: { label: 'Afternoon', icon: Sunset, accent: '#8DA399', bg: 'bg-[#8DA399]/5', border: 'border-[#8DA399]/20', text: 'text-[#8DA399]' },
  evening: { label: 'Evening', icon: Moon, accent: '#B37E6B', bg: 'bg-[#B37E6B]/5', border: 'border-[#B37E6B]/20', text: 'text-[#B37E6B]' },
};

const VISIBILITY_OPTIONS = [
  { value: 'draft', label: 'Draft', desc: 'Advisor only', color: 'bg-gray-100 text-gray-600' },
  { value: 'suggested', label: 'Suggested', desc: 'Optional visibility', color: 'bg-amber-50 text-amber-700' },
  { value: 'approved', label: 'Approved', desc: 'Client visible', color: 'bg-emerald-50 text-emerald-700' },
  { value: 'hidden', label: 'Hidden', desc: 'Fully invisible', color: 'bg-red-50 text-red-600' },
  { value: 'scheduled_reveal', label: 'Scheduled', desc: 'Auto unlock', color: 'bg-purple-50 text-purple-600' },
];

const ITEM_TYPES = [
  { value: 'meal', label: 'Meal' },
  { value: 'activity', label: 'Activity' },
  { value: 'rest', label: 'Rest / Reset' },
  { value: 'transport', label: 'Transport' },
  { value: 'custom', label: 'Custom' },
];

const ADVISOR_MODES = [
  { id: 'overview', label: 'Overview', icon: LayoutGrid },
  { id: 'plan-day', label: 'Plan Day', icon: CalendarDays },
  { id: 'client-preview', label: 'Client Preview', icon: Monitor },
];

// Parse date safely as local
function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  const parts = dateStr.split('T')[0].split('-');
  return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
}

function formatDayHeader(day) {
  const d = parseLocalDate(day.date);
  if (!d) return `Day ${day.day_number}`;
  const dow = d.toLocaleDateString('en-US', { weekday: 'long' });
  const md = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  return `Day ${day.day_number} — ${dow}, ${md}`;
}

function getVisStyle(vis) {
  return VISIBILITY_OPTIONS.find(v => v.value === vis) || VISIBILITY_OPTIONS[0];
}

// ============================================================
// ITEM EDITOR (inline)
// ============================================================
function ItemEditor({ item, onSave, onCancel, blockType }) {
  const [form, setForm] = useState({
    name: item?.name || '',
    item_type: item?.item_type || 'activity',
    time: item?.time || '',
    address: item?.address || '',
    website: item?.website || '',
    phone: item?.phone || '',
    booking_link: item?.booking_link || '',
    price_estimate: item?.price_estimate || '',
    notes: item?.notes || '',
    image_url: item?.image_url || '',
    visibility: item?.visibility || 'draft',
    scheduled_reveal_at: item?.scheduled_reveal_at || '',
    teaser_text: item?.teaser_text || '',
  });

  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    onSave({ ...item, ...form, id: item?.id });
  };

  return (
    <div className="space-y-3 p-3 bg-white rounded-lg border border-[rgba(0,0,0,0.08)]" data-testid={`item-editor-${blockType}`}>
      <div className="grid grid-cols-2 gap-2">
        <Input
          placeholder="Name (e.g. Café de Flore)"
          value={form.name}
          onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
          className="col-span-2 text-sm"
          data-testid="item-name-input"
          autoFocus
        />
        <Select value={form.item_type} onValueChange={v => setForm(p => ({ ...p, item_type: v }))}>
          <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ITEM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          placeholder="Time (e.g. 9:00 AM)"
          value={form.time}
          onChange={e => setForm(p => ({ ...p, time: e.target.value }))}
          className="text-xs h-8"
        />
      </div>
      <Input
        placeholder="Address"
        value={form.address}
        onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
        className="text-xs h-8"
      />
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Website" value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} className="text-xs h-8" />
        <Input placeholder="Phone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="text-xs h-8" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Booking Link" value={form.booking_link} onChange={e => setForm(p => ({ ...p, booking_link: e.target.value }))} className="text-xs h-8" />
        <Input placeholder="Price Est." value={form.price_estimate} onChange={e => setForm(p => ({ ...p, price_estimate: e.target.value }))} className="text-xs h-8" />
      </div>
      <Textarea
        placeholder="Notes..."
        value={form.notes}
        onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
        className="text-xs min-h-[50px]"
      />
      <Input
        placeholder="Image URL (optional)"
        value={form.image_url}
        onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))}
        className="text-xs h-8"
      />
      {/* Visibility */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#8C8C8C] w-16">Visibility:</span>
        <Select value={form.visibility} onValueChange={v => setForm(p => ({ ...p, visibility: v }))}>
          <SelectTrigger className="text-xs h-7 flex-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            {VISIBILITY_OPTIONS.map(v => (
              <SelectItem key={v.value} value={v.value}>
                <span className={`text-xs px-1.5 py-0.5 rounded ${v.color}`}>{v.label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {form.visibility === 'scheduled_reveal' && (
        <Input
          type="datetime-local"
          value={form.scheduled_reveal_at?.slice(0, 16) || ''}
          onChange={e => setForm(p => ({ ...p, scheduled_reveal_at: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
          className="text-xs h-8"
        />
      )}
      {(form.visibility === 'hidden' || form.visibility === 'scheduled_reveal') && (
        <Input
          placeholder="Teaser text (e.g. Something special awaits...)"
          value={form.teaser_text}
          onChange={e => setForm(p => ({ ...p, teaser_text: e.target.value }))}
          className="text-xs h-8"
        />
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} className="text-xs h-7"><X className="w-3 h-3 mr-1" />Cancel</Button>
        <Button size="sm" onClick={handleSubmit} className="text-xs h-7 bg-[#C8A85A] hover:bg-[#B39049]"><Check className="w-3 h-3 mr-1" />Save</Button>
      </div>
    </div>
  );
}

// ============================================================
// ITEM CARD (display mode)
// ============================================================
function ItemCard({ item, slotKey, blockType, onEdit, onDelete }) {
  if (!item || !item.name) return null;
  const vis = getVisStyle(item.visibility || 'draft');
  const blockConfig = BLOCKS[blockType];

  const slotLabels = { meal_slot: 'Meal', primary_activity: 'Activity', secondary_activity: 'Optional' };

  return (
    <div
      className={`group relative p-3 rounded-lg border transition-all hover:shadow-sm ${blockConfig.bg} ${blockConfig.border}`}
      data-testid={`item-card-${blockType}-${slotKey}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] uppercase tracking-wider font-medium ${blockConfig.text}`}>
              {slotLabels[slotKey]}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${vis.color}`}>{vis.label}</span>
          </div>
          <h4 className="text-sm font-medium text-[#1A1A1A] truncate">{item.name}</h4>
          {item.time && (
            <div className="flex items-center gap-1 mt-1 text-xs text-[#8C8C8C]">
              <Clock className="w-3 h-3" />{item.time}
            </div>
          )}
          {item.address && (
            <div className="flex items-center gap-1 mt-0.5 text-xs text-[#8C8C8C]">
              <MapPin className="w-3 h-3" /><span className="truncate">{item.address}</span>
            </div>
          )}
          {item.notes && <p className="text-xs text-[#8C8C8C] mt-1 line-clamp-2">{item.notes}</p>}
        </div>
        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onEdit(item)} className="p-1 rounded hover:bg-black/5" data-testid={`edit-${blockType}-${slotKey}`}>
            <Edit2 className="w-3.5 h-3.5 text-[#8C8C8C]" />
          </button>
          <button onClick={() => onDelete()} className="p-1 rounded hover:bg-red-50" data-testid={`delete-${blockType}-${slotKey}`}>
            <Trash2 className="w-3.5 h-3.5 text-red-400" />
          </button>
        </div>
      </div>
      {/* Quick links */}
      {(item.website || item.booking_link) && (
        <div className="flex gap-2 mt-2">
          {item.website && (
            <a href={item.website} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#C5A059] hover:underline flex items-center gap-0.5">
              <ExternalLink className="w-3 h-3" />Web
            </a>
          )}
          {item.booking_link && (
            <a href={item.booking_link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#8DA399] hover:underline flex items-center gap-0.5">
              <ExternalLink className="w-3 h-3" />Book
            </a>
          )}
        </div>
      )}
      {/* Item image */}
      {item.image_url && (
        <img src={item.image_url} alt="" className="mt-2 rounded-lg w-full h-24 object-cover" onError={e => e.target.style.display = 'none'} />
      )}
    </div>
  );
}

// ============================================================
// SLOT (empty state + filled state)
// ============================================================
function Slot({ item, slotKey, blockType, dayNumber, tripId, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const blockConfig = BLOCKS[blockType];
  const slotLabels = { meal_slot: 'Meal', primary_activity: 'Activity', secondary_activity: 'Optional' };
  const slotIcons = { meal_slot: Utensils, primary_activity: MapPin, secondary_activity: Sparkles };
  const SlotIcon = slotIcons[slotKey];

  const handleSave = async (itemData) => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/itinerary-engine/trips/${tripId}/days/${dayNumber}/block/${blockType}/slot/${slotKey}`,
        itemData
      );
      toast.success(`${slotLabels[slotKey]} saved`);
      setEditing(false);
      onUpdate();
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/itinerary-engine/trips/${tripId}/days/${dayNumber}/block/${blockType}/slot/${slotKey}`);
      toast.success('Removed');
      onUpdate();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  if (editing) {
    return <ItemEditor item={item} onSave={handleSave} onCancel={() => setEditing(false)} blockType={blockType} />;
  }

  if (item && item.name) {
    return <ItemCard item={item} slotKey={slotKey} blockType={blockType} onEdit={() => setEditing(true)} onDelete={handleDelete} />;
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className={`w-full p-3 rounded-lg border border-dashed ${blockConfig.border} hover:${blockConfig.bg} transition-all flex items-center gap-2 text-left`}
      data-testid={`add-${blockType}-${slotKey}`}
    >
      <div className={`p-1 rounded ${blockConfig.bg}`}>
        <SlotIcon className={`w-3.5 h-3.5 ${blockConfig.text}`} />
      </div>
      <span className="text-xs text-[#8C8C8C]">Add {slotLabels[slotKey]}</span>
      <Plus className="w-3 h-3 text-[#8C8C8C] ml-auto" />
    </button>
  );
}

// ============================================================
// BLOCK (Morning / Afternoon / Evening)
// ============================================================
function Block({ block, blockType, dayNumber, tripId, onUpdate }) {
  const config = BLOCKS[blockType];
  const Icon = config.icon;
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className={`rounded-xl border ${config.border} overflow-hidden`} data-testid={`block-${blockType}`}>
      <button
        onClick={() => setCollapsed(!collapsed)}
        className={`w-full flex items-center gap-2 px-4 py-2.5 ${config.bg} transition-colors`}
      >
        <Icon className={`w-4 h-4 ${config.text}`} />
        <span className={`text-sm font-medium ${config.text}`}>{config.label}</span>
        <div className="ml-auto">
          {collapsed ? <ChevronDown className="w-4 h-4 text-[#8C8C8C]" /> : <ChevronUp className="w-4 h-4 text-[#8C8C8C]" />}
        </div>
      </button>
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="px-4 py-3 space-y-2"
          >
            <Slot item={block?.meal_slot} slotKey="meal_slot" blockType={blockType} dayNumber={dayNumber} tripId={tripId} onUpdate={onUpdate} />
            <Slot item={block?.primary_activity} slotKey="primary_activity" blockType={blockType} dayNumber={dayNumber} tripId={tripId} onUpdate={onUpdate} />
            <Slot item={block?.secondary_activity} slotKey="secondary_activity" blockType={blockType} dayNumber={dayNumber} tripId={tripId} onUpdate={onUpdate} />
            {block?.notes && <p className="text-xs text-[#8C8C8C] italic px-1">{block.notes}</p>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================
// DAY CARD (Overview mode)
// ============================================================
function DayOverviewCard({ day, onSelect, isActive }) {
  const vis = getVisStyle(day.visibility);
  const filledCount = ['morning', 'afternoon', 'evening'].reduce((sum, bk) => {
    const block = day[bk];
    if (!block) return sum;
    return sum + (block.meal_slot?.name ? 1 : 0) + (block.primary_activity?.name ? 1 : 0) + (block.secondary_activity?.name ? 1 : 0);
  }, 0);
  const d = parseLocalDate(day.date);
  const dow = d ? d.toLocaleDateString('en-US', { weekday: 'short' }) : '';
  const md = d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

  return (
    <button
      onClick={() => onSelect(day.day_number)}
      className={`w-full text-left p-3 rounded-xl border transition-all hover:shadow-md ${isActive ? 'border-[#C8A85A] bg-[#C8A85A]/5 shadow-sm' : 'border-[rgba(0,0,0,0.06)] bg-white'}`}
      data-testid={`day-overview-${day.day_number}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#1A1A1A]">Day {day.day_number}</span>
          {day.is_arrival_day && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">Arrival</span>}
          {day.is_departure_day && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">Departure</span>}
          {day.is_travel_day && !day.is_arrival_day && !day.is_departure_day && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-500">Travel</span>}
          {day.is_rest_day && <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-600">Rest</span>}
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${vis.color}`}>{vis.label}</span>
      </div>
      <div className="text-xs text-[#8C8C8C]">{dow} {md}</div>
      {day.destination_name && day.destination_name !== 'Travel Day' && (
        <div className="text-xs text-[#1A1A1A] mt-1 flex items-center gap-1">
          <MapPin className="w-3 h-3 text-[#C5A059]" />{day.destination_name}
        </div>
      )}
      <div className="flex items-center gap-1 mt-2">
        <div className="flex-1 bg-gray-100 rounded-full h-1.5">
          <div className="bg-[#C8A85A] h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (filledCount / 9) * 100)}%` }} />
        </div>
        <span className="text-[10px] text-[#8C8C8C]">{filledCount}/9</span>
      </div>
    </button>
  );
}

// ============================================================
// DAY PLANNER (Plan Day mode)
// ============================================================
function DayPlanner({ day, tripId, onUpdate }) {
  const [dayVis, setDayVis] = useState(day.visibility || 'draft');
  const [revealAt, setRevealAt] = useState(day.scheduled_reveal_at || '');
  const [teaser, setTeaser] = useState(day.teaser_text || '');
  const [dayNotes, setDayNotes] = useState(day.day_notes || '');
  const [savingMeta, setSavingMeta] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [imageUrl, setImageUrl] = useState(day.day_image_url || '');
  const [editingImage, setEditingImage] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setDayVis(day.visibility || 'draft');
    setRevealAt(day.scheduled_reveal_at || '');
    setTeaser(day.teaser_text || '');
    setDayNotes(day.day_notes || '');
    setAiSuggestions(null);
    setImageUrl(day.day_image_url || '');
    setEditingImage(false);
    setImageError(false);
  }, [day]);

  const handleAiSuggest = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/itinerary-engine/trips/${tripId}/days/${day.day_number}/ai-suggest`, {
        focus: 'full_day',
      });
      if (res.data.success && res.data.suggestions) {
        setAiSuggestions(res.data.suggestions);
        toast.success('AI suggestions ready — review and apply');
      }
    } catch (e) {
      toast.error('AI suggestion failed');
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiSuggestions = async () => {
    if (!aiSuggestions) return;
    try {
      const res = await axios.post(`${API}/itinerary-engine/trips/${tripId}/days/${day.day_number}/apply-ai`, {
        suggestions: aiSuggestions,
      });
      toast.success(`Applied ${res.data.applied_count} suggestions`);
      setAiSuggestions(null);
      onUpdate();
    } catch {
      toast.error('Failed to apply suggestions');
    }
  };

  const approveAllItems = async () => {
    try {
      const res = await axios.post(`${API}/itinerary-engine/trips/${tripId}/days/${day.day_number}/approve-all-items`);
      toast.success(`Day approved with ${res.data.approved_items} items`);
      setDayVis('approved');
      onUpdate();
    } catch {
      toast.error('Failed to approve');
    }
  };

  const saveDayImage = async (url) => {
    try {
      await axios.post(`${API}/itinerary-engine/trips/${tripId}/days/${day.day_number}/image`, {
        image_url: url,
        image_source: 'manual',
      });
      setImageUrl(url);
      setEditingImage(false);
      toast.success('Day image saved');
      onUpdate();
    } catch {
      toast.error('Failed to save image');
    }
  };

  const saveDayMeta = async () => {
    setSavingMeta(true);
    try {
      await axios.patch(`${API}/itinerary-engine/trips/${tripId}/days/${day.day_number}`, {
        visibility: dayVis,
        scheduled_reveal_at: dayVis === 'scheduled_reveal' ? revealAt : null,
        teaser_text: teaser || null,
        day_notes: dayNotes || null,
      });
      toast.success('Day settings saved');
      onUpdate();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSavingMeta(false);
    }
  };

  return (
    <div className="space-y-4" data-testid={`day-planner-${day.day_number}`}>
      {/* Day Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 pb-3 border-b border-[rgba(0,0,0,0.06)]">
        <div>
          <h3 className="font-serif text-lg text-[#1A1A1A]">{formatDayHeader(day)}</h3>
          <div className="flex items-center gap-2 mt-1">
            {day.destination_name && <span className="text-xs text-[#8C8C8C] flex items-center gap-1"><MapPin className="w-3 h-3" />{day.destination_name}</span>}
            {day.is_arrival_day && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">Arrival Day</span>}
            {day.is_departure_day && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">Departure Day</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={dayVis} onValueChange={v => setDayVis(v)}>
            <SelectTrigger className="text-xs h-8 w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {VISIBILITY_OPTIONS.map(v => <SelectItem key={v.value} value={v.value}>{v.label} — {v.desc}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={saveDayMeta} disabled={savingMeta} className="text-xs h-8" data-testid="save-day-meta">
            {savingMeta ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            Save
          </Button>
          <Button size="sm" onClick={handleAiSuggest} disabled={aiLoading} className="text-xs h-8 bg-[#C8A85A] hover:bg-[#B39049]" data-testid="ai-suggest-day">
            {aiLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
            AI Plan
          </Button>
          <Button size="sm" variant="outline" onClick={approveAllItems} className="text-xs h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="approve-all-items">
            <Check className="w-3 h-3 mr-1" />Approve All
          </Button>
        </div>
      </div>

      {/* Day Image */}
      <div className="rounded-xl overflow-hidden border border-[rgba(0,0,0,0.06)]" data-testid="day-image-section">
        {imageUrl && !editingImage && !imageError ? (
          <div className="relative group">
            <img
              src={imageUrl}
              alt={`Day ${day.day_number}`}
              className="w-full h-36 object-cover"
              onError={() => setImageError(true)}
            />
            <button
              onClick={() => setEditingImage(true)}
              className="absolute top-2 right-2 bg-white/80 backdrop-blur-sm rounded-lg p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid="edit-day-image"
            >
              <Edit2 className="w-3.5 h-3.5 text-[#1A1A1A]" />
            </button>
            <span className="absolute bottom-2 left-2 text-[10px] px-2 py-0.5 rounded bg-black/50 text-white">
              {day.day_image_source || 'manual'}
            </span>
          </div>
        ) : (
          <div className="bg-[#F9F8F4] p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Image className="w-3.5 h-3.5 text-[#8C8C8C]" />
              <span className="text-xs text-[#8C8C8C]">Day Cover Image</span>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Paste image URL..."
                value={imageUrl}
                onChange={e => { setImageUrl(e.target.value); setImageError(false); }}
                className="text-xs h-8 flex-1"
                data-testid="day-image-url-input"
              />
              <Button size="sm" onClick={() => saveDayImage(imageUrl)} disabled={!imageUrl.trim()} className="text-xs h-8 bg-[#C8A85A] hover:bg-[#B39049]">
                <Check className="w-3 h-3" />
              </Button>
              {(editingImage || imageError) && day.day_image_url && (
                <Button size="sm" variant="ghost" onClick={() => { setEditingImage(false); setImageUrl(day.day_image_url || ''); setImageError(false); }} className="text-xs h-8">
                  <X className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Scheduled reveal controls */}
      {dayVis === 'scheduled_reveal' && (
        <div className="bg-purple-50/50 rounded-lg p-3 space-y-2">
          <label className="text-xs font-medium text-purple-700">Reveal Date & Time</label>
          <Input
            type="datetime-local"
            value={revealAt?.slice(0, 16) || ''}
            onChange={e => setRevealAt(e.target.value ? new Date(e.target.value).toISOString() : '')}
            className="text-xs h-8"
          />
        </div>
      )}

      {/* Teaser for hidden/scheduled */}
      {(dayVis === 'hidden' || dayVis === 'scheduled_reveal') && (
        <div className="bg-[#F9F8F4] rounded-lg p-3 space-y-2">
          <label className="text-xs font-medium text-[#1A1A1A]">Client Teaser Message</label>
          <Input
            placeholder="Tomorrow holds something unforgettable..."
            value={teaser}
            onChange={e => setTeaser(e.target.value)}
            className="text-xs h-8"
          />
        </div>
      )}

      {/* Rest day indicator */}
      {day.is_rest_day && (
        <div className="bg-teal-50 rounded-lg p-4 text-center">
          <p className="text-sm text-teal-700 font-medium">Rest & Reset Day</p>
          {day.rest_message && <p className="text-xs text-teal-600 mt-1">{day.rest_message}</p>}
        </div>
      )}

      {/* AI Suggestions Panel */}
      {aiSuggestions && (
        <div className="bg-[#C8A85A]/5 rounded-xl border border-[#C8A85A]/20 p-4 space-y-3" data-testid="ai-suggestions-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#C8A85A]" />
              <span className="text-sm font-medium text-[#1A1A1A]">AI Suggestions</span>
              <span className="text-xs text-[#8C8C8C]">Review before applying</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setAiSuggestions(null)} className="text-xs h-7">
                <X className="w-3 h-3 mr-1" />Dismiss
              </Button>
              <Button size="sm" onClick={applyAiSuggestions} className="text-xs h-7 bg-[#C8A85A] hover:bg-[#B39049]">
                <Check className="w-3 h-3 mr-1" />Apply to Empty Slots
              </Button>
            </div>
          </div>
          <div className="grid gap-2">
            {['morning', 'afternoon', 'evening'].map(bk => {
              const blockSuggestions = aiSuggestions[bk];
              if (!blockSuggestions) return null;
              const config = BLOCKS[bk];
              return (
                <div key={bk} className="text-xs space-y-1">
                  <span className={`font-medium ${config.text}`}>{config.label}:</span>
                  {blockSuggestions.meal_slot?.name && <div className="pl-4 text-[#595959]">Meal: {blockSuggestions.meal_slot.name} {blockSuggestions.meal_slot.time && `(${blockSuggestions.meal_slot.time})`}</div>}
                  {blockSuggestions.primary_activity?.name && <div className="pl-4 text-[#595959]">Activity: {blockSuggestions.primary_activity.name} {blockSuggestions.primary_activity.time && `(${blockSuggestions.primary_activity.time})`}</div>}
                  {blockSuggestions.secondary_activity?.name && <div className="pl-4 text-[#595959]">Optional: {blockSuggestions.secondary_activity.name}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Blocks */}
      <div className="space-y-3">
        {['morning', 'afternoon', 'evening'].map(bk => (
          <Block
            key={bk}
            block={day[bk]}
            blockType={bk}
            dayNumber={day.day_number}
            tripId={tripId}
            onUpdate={onUpdate}
          />
        ))}
      </div>

      {/* Day Notes */}
      <div className="bg-[#F9F8F4] rounded-lg p-3">
        <label className="text-xs font-medium text-[#1A1A1A] mb-1 block">Day Notes (advisor only)</label>
        <Textarea
          placeholder="Internal planning notes..."
          value={dayNotes}
          onChange={e => setDayNotes(e.target.value)}
          className="text-xs min-h-[60px]"
        />
      </div>
    </div>
  );
}

// ============================================================
// CLIENT PREVIEW (advisor-side preview of what client sees)
// ============================================================
function ClientPreviewDay({ day }) {
  if (day.is_hidden) {
    return (
      <div className="bg-[#F9F8F4] rounded-xl p-8 text-center" data-testid={`preview-hidden-${day.day_number}`}>
        <Lock className="w-8 h-8 text-[#C8A85A] mx-auto mb-3" />
        <p className="text-sm text-[#1A1A1A] font-medium italic">
          {day.teaser_text || 'Something special awaits...'}
        </p>
      </div>
    );
  }

  const d = parseLocalDate(day.date);
  const dow = d ? d.toLocaleDateString('en-US', { weekday: 'long' }) : '';
  const md = d ? d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : '';

  return (
    <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] overflow-hidden" data-testid={`preview-day-${day.day_number}`}>
      <div className="px-5 py-4 border-b border-[rgba(0,0,0,0.04)] bg-[#F9F8F4]">
        <h3 className="font-serif text-lg text-[#1A1A1A]">Day {day.day_number}</h3>
        <p className="text-xs text-[#8C8C8C]">{dow}, {md}</p>
        {day.destination_name && <p className="text-xs text-[#C5A059] mt-1">{day.destination_name}</p>}
      </div>
      <div className="p-5 space-y-4">
        {['morning', 'afternoon', 'evening'].map(bk => {
          const block = day[bk];
          if (!block) return null;
          const config = BLOCKS[bk];
          const Icon = config.icon;
          const hasContent = block.meal_slot?.name || block.primary_activity?.name || block.secondary_activity?.name;
          if (!hasContent) return null;

          return (
            <div key={bk}>
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${config.text}`} />
                <span className={`text-xs font-medium uppercase tracking-wider ${config.text}`}>{config.label}</span>
              </div>
              <div className="space-y-1.5 pl-6">
                {block.meal_slot?.name && (
                  <div className="flex items-center gap-2">
                    <Utensils className="w-3 h-3 text-[#8C8C8C]" />
                    <span className="text-sm text-[#1A1A1A]">{block.meal_slot.name}</span>
                    {block.meal_slot.time && <span className="text-xs text-[#8C8C8C]">{block.meal_slot.time}</span>}
                  </div>
                )}
                {block.primary_activity?.name && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3 h-3 text-[#8C8C8C]" />
                    <span className="text-sm text-[#1A1A1A]">{block.primary_activity.name}</span>
                    {block.primary_activity.time && <span className="text-xs text-[#8C8C8C]">{block.primary_activity.time}</span>}
                  </div>
                )}
                {block.secondary_activity?.name && (
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-3 h-3 text-[#8C8C8C]" />
                    <span className="text-sm text-[#1A1A1A]">{block.secondary_activity.name}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// MAIN: ITINERARY ENGINE
// ============================================================
export function ItineraryEngine({ tripId, startDate, endDate, onNavigateToAI }) {
  const [days, setDays] = useState([]);
  const [clientDays, setClientDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [mode, setMode] = useState('overview');
  const [selectedDay, setSelectedDay] = useState(1);
  const [duplicateWarnings, setDuplicateWarnings] = useState([]);
  const [showDuplicates, setShowDuplicates] = useState(false);

  const fetchDays = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/itinerary-engine/trips/${tripId}/days`);
      setDays(res.data.days || []);
      setDuplicateWarnings(res.data.duplicate_warnings || []);
    } catch (e) {
      console.error('Failed to fetch itinerary days:', e);
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  const fetchClientDays = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/itinerary-engine/trips/${tripId}/days/client`);
      setClientDays(res.data.days || []);
    } catch (e) {
      console.error('Failed to fetch client days:', e);
    }
  }, [tripId]);

  useEffect(() => {
    if (tripId) {
      fetchDays();
    }
  }, [tripId, fetchDays]);

  useEffect(() => {
    if (mode === 'client-preview') {
      fetchClientDays();
    }
  }, [mode, fetchClientDays]);

  const generateDays = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/itinerary-engine/trips/${tripId}/generate`);
      toast.success(`Generated ${res.data.total_days} itinerary days`);
      setDays(res.data.days || []);
      setSelectedDay(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate days');
    } finally {
      setGenerating(false);
    }
  };

  const currentDay = useMemo(() => days.find(d => d.day_number === selectedDay), [days, selectedDay]);

  const summary = useMemo(() => {
    const total = days.length;
    const approved = days.filter(d => d.visibility === 'approved').length;
    let filled = 0;
    let slots = 0;
    days.forEach(d => {
      ['morning', 'afternoon', 'evening'].forEach(bk => {
        const block = d[bk];
        if (!block) return;
        ['meal_slot', 'primary_activity', 'secondary_activity'].forEach(sk => {
          slots++;
          if (block[sk]?.name) filled++;
        });
      });
    });
    return { total, approved, filled, slots, pct: slots ? Math.round((filled / slots) * 100) : 0 };
  }, [days]);

  // Bulk approve all days AND their items
  const bulkApproveAll = async () => {
    try {
      // Approve each day and its items
      const promises = days.map(d =>
        axios.post(`${API}/itinerary-engine/trips/${tripId}/days/${d.day_number}/approve-all-items`)
      );
      await Promise.all(promises);
      toast.success('All days and items approved');
      fetchDays();
    } catch {
      toast.error('Failed to bulk approve');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#C8A85A]" />
      </div>
    );
  }

  // No days generated yet
  if (days.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-8 text-center" data-testid="itinerary-empty">
        <Calendar className="w-10 h-10 text-[#C8A85A] mx-auto mb-4" />
        <h3 className="font-serif text-lg text-[#1A1A1A] mb-2">Build Your Itinerary</h3>
        <p className="text-sm text-[#8C8C8C] mb-6 max-w-md mx-auto">
          Generate day-by-day planning structure from your trip dates and destinations.
        </p>
        <Button
          onClick={generateDays}
          disabled={generating}
          className="bg-[#C8A85A] hover:bg-[#B39049]"
          data-testid="generate-itinerary-btn"
        >
          {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarDays className="w-4 h-4 mr-2" />}
          Generate Itinerary Days
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="itinerary-engine">
      {/* Header Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#8C8C8C]">
              {summary.total} days • {summary.filled}/{summary.slots} items ({summary.pct}%) • {summary.approved} approved
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Mode Tabs */}
          <div className="flex bg-[#F9F8F4] rounded-lg p-0.5">
            {ADVISOR_MODES.map(m => {
              const MIcon = m.icon;
              return (
                <button
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-all ${mode === m.id ? 'bg-white shadow-sm text-[#1A1A1A] font-medium' : 'text-[#8C8C8C] hover:text-[#1A1A1A]'}`}
                  data-testid={`mode-${m.id}`}
                >
                  <MIcon className="w-3.5 h-3.5" />{m.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Duplicate Warnings */}
      {duplicateWarnings.length > 0 && (
        <button
          onClick={() => setShowDuplicates(!showDuplicates)}
          className="w-full flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-left"
          data-testid="duplicate-warning"
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span className="text-xs text-amber-700 flex-1">
            {duplicateWarnings.length} duplicate{duplicateWarnings.length > 1 ? 's' : ''} detected across your itinerary
          </span>
          <ChevronDown className={`w-4 h-4 text-amber-500 transition-transform ${showDuplicates ? 'rotate-180' : ''}`} />
        </button>
      )}
      <AnimatePresence>
        {showDuplicates && duplicateWarnings.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-amber-50/50 rounded-lg p-3 space-y-2 overflow-hidden"
          >
            {duplicateWarnings.map((w, i) => (
              <div key={i} className="text-xs">
                <span className="font-medium text-amber-800">"{w.display_name}"</span>
                <span className="text-amber-600"> appears on days: </span>
                {w.occurrences.map((o, j) => (
                  <button
                    key={j}
                    onClick={() => { setSelectedDay(o.day_number); setMode('plan-day'); }}
                    className="text-amber-700 underline hover:text-amber-900 mx-0.5"
                  >
                    Day {o.day_number}
                  </button>
                ))}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== OVERVIEW MODE ==================== */}
      {mode === 'overview' && (
        <div data-testid="overview-mode">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-base text-[#1A1A1A]">Trip Overview</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={bulkApproveAll} className="text-xs h-7">
                <Check className="w-3 h-3 mr-1" />Approve All
              </Button>
              <Button size="sm" variant="outline" onClick={generateDays} disabled={generating} className="text-xs h-7">
                <RefreshCw className={`w-3 h-3 mr-1 ${generating ? 'animate-spin' : ''}`} />Regenerate
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {days.map(day => (
              <DayOverviewCard
                key={day.id}
                day={day}
                isActive={selectedDay === day.day_number}
                onSelect={(n) => { setSelectedDay(n); setMode('plan-day'); }}
              />
            ))}
          </div>
        </div>
      )}

      {/* ==================== PLAN DAY MODE ==================== */}
      {mode === 'plan-day' && (
        <div data-testid="plan-day-mode">
          {/* Day Navigator */}
          <div className="flex items-center gap-2 mb-4 bg-[#F9F8F4] rounded-lg p-2">
            <Button
              variant="ghost" size="sm"
              onClick={() => setSelectedDay(Math.max(1, selectedDay - 1))}
              disabled={selectedDay <= 1}
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex-1 flex gap-1 overflow-x-auto py-1 scrollbar-hide">
              {days.map(d => {
                const vis = getVisStyle(d.visibility);
                return (
                  <button
                    key={d.day_number}
                    onClick={() => setSelectedDay(d.day_number)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-md text-xs transition-all ${selectedDay === d.day_number ? 'bg-white shadow-sm font-medium text-[#1A1A1A]' : 'text-[#8C8C8C] hover:bg-white/50'}`}
                    data-testid={`nav-day-${d.day_number}`}
                  >
                    <div>{d.day_number}</div>
                    <div className={`w-1.5 h-1.5 rounded-full mx-auto mt-0.5`} style={{ backgroundColor: vis.value === 'approved' ? '#8DA399' : vis.value === 'draft' ? '#ccc' : vis.value === 'hidden' ? '#ef4444' : '#C8A85A' }} />
                  </button>
                );
              })}
            </div>
            <Button
              variant="ghost" size="sm"
              onClick={() => setSelectedDay(Math.min(days.length, selectedDay + 1))}
              disabled={selectedDay >= days.length}
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Day Planner */}
          {currentDay && (
            <DayPlanner
              day={currentDay}
              tripId={tripId}
              onUpdate={fetchDays}
            />
          )}
        </div>
      )}

      {/* ==================== CLIENT PREVIEW MODE ==================== */}
      {mode === 'client-preview' && (
        <div className="space-y-4" data-testid="client-preview-mode">
          <div className="bg-[#F9F8F4] rounded-lg p-3 flex items-center gap-2">
            <Eye className="w-4 h-4 text-[#C8A85A]" />
            <span className="text-xs text-[#8C8C8C]">
              Client Preview — showing only approved & revealed content ({clientDays.filter(d => !d.is_hidden).length} visible days)
            </span>
          </div>
          {clientDays.length === 0 ? (
            <div className="text-center py-12">
              <EyeOff className="w-8 h-8 text-[#8C8C8C] mx-auto mb-3" />
              <p className="text-sm text-[#8C8C8C]">No days are visible to the client yet. Set day visibility to "Approved" to show them.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {clientDays.map(day => (
                <ClientPreviewDay key={day.id || day.day_number} day={day} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
